<script setup lang="ts">
import store from "@/store";
import { computed, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";

import { toast } from "@/common/utils/toast";
import { getDate, getHour, getDifference } from "@/common/utils/dates";

import {
  baseConfirm,
  baseDate,
  baseTextarea,
  svgIcon,
} from "@/common/components";

import {
  evaluationStart,
  evaluationCancel,
  evaluationFinish,
  evaluationPatch,
} from "@/common/api/rest/evaluation/evaluation";

import { EvaluationType } from "@/common/types/evaluation";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const emit = defineEmits(["update-data"]);

const props = withDefaults(
  defineProps<{
    data: EvaluationType | null;
    width?: string;
    showTitle?: boolean;
    actionShow?: boolean;
  }>(),
  {
    width: "100%",
    showTitle: true,
    actionShow: true,
  },
);

const payload = ref({
  period_start_date: "",
  period_end_date: "",
  description: "",
});

const showConfirmStart = ref(false);
const showConfirmFinish = ref(false);
const showConfirmCancel = ref(false);

const accountId = computed(() => route.params.id as string);

function checkOptionsPermission() {
  return true;
  // return store.getters.type === "ADMIN" || store.getters.type === "MANAGER";
}

function showActions(status: string) {
  return status === "PENDING" || status === "STARTED";
}

function back() {
  router.push(`/${accountId.value}/cockpit`);
}

function patchData(payload: Partial<EvaluationType>) {
  if (!props.data) return;

  evaluationPatch(accountId.value, props.data.evaluation_id, payload)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        emit("update-data");
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function startEvaluation() {
  showConfirmStart.value = true;
}

function startEvaluationCancel() {
  showConfirmStart.value = false;
}

function startEvaluationConfirm() {
  if (!props.data) return;

  startEvaluationCancel();
  store.commit("SET_LOADING", true);

  evaluationStart(accountId.value, props.data.evaluation_id)
    .then((response) => {
      const { success, data } = response;

      if (success) {
        emit("update-data");
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function finishEvaluation() {
  showConfirmFinish.value = true;
}

function finishEvaluationCancel() {
  showConfirmFinish.value = false;
}

function finishEvaluationConfirm() {
  if (!props.data) return;

  finishEvaluationCancel();
  store.commit("SET_LOADING", true);

  evaluationFinish(accountId.value, props.data.evaluation_id)
    .then((response) => {
      const { success, data } = response;

      if (success) {
        emit("update-data");
        back();
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function cancelEvaluation() {
  showConfirmCancel.value = true;
}

function cancelEvaluationCancel() {
  showConfirmCancel.value = false;
}

function cancelEvaluationConfirm() {
  if (!props.data) return;

  cancelEvaluationCancel();
  store.commit("SET_LOADING", true);

  evaluationCancel(accountId.value, props.data.evaluation_id)
    .then((response) => {
      const { success, data } = response;

      if (success) {
        emit("update-data");
        back();
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function updatePeriod(key: keyof typeof payload.value) {
  patchData({ [key]: payload.value[key] });
}

function updateDescription() {
  patchData({ description: payload.value.description });
}

watchEffect(() => {
  if (props.data) {
    const { period_start_date, period_end_date, description } = props.data;
    payload.value = {
      period_start_date: period_start_date ?? "",
      period_end_date: period_end_date ?? "",
      description: description ?? "",
    };
  }
});
</script>

<template>
  <section v-if="data" class="tab-component" :class="{ width: width }">
    <template v-if="checkOptionsPermission() && showActions(data.status)">
      <template v-if="showTitle">
        <div class="title">
          {{ t("common.labels.options") }}
        </div>
      </template>

      <div class="content">
        <template v-if="actionShow">
          <div class="box-actions">
            <template v-if="props.data?.start_date">
              <button
                type="button"
                class="btn btn-black"
                @click="finishEvaluation()"
              >
                <svg-icon icon="pause" size="1.5rem" />
                {{ t("common.labels.finish").toUpperCase() }}
              </button>
            </template>
            <template v-else>
              <button
                type="button"
                class="btn btn-primary"
                @click="startEvaluation()"
              >
                <svg-icon icon="play" size="1.5rem" />
                {{ t("common.labels.play").toUpperCase() }}
              </button>
            </template>

            <button
              type="button"
              class="btn btn-danger"
              @click="cancelEvaluation()"
            >
              <svg-icon icon="close" size="1.5rem" />
              {{ t("common.labels.cancel").toUpperCase() }}
            </button>
          </div>
        </template>
      </div>
    </template>

    <div class="content">
      <div class="periods">
        <base-date
          v-model="payload.period_start_date"
          :value="payload.period_start_date"
          value-mask="YYYY-MM-DD"
          placeholder="--/--/----"
          @update:model-value="updatePeriod('period_start_date')"
        >
          <template #label>
            {{ t("evaluation.labels.start_forecast") }}
          </template>
        </base-date>

        <base-date
          v-model="payload.period_end_date"
          :value="payload.period_end_date"
          value-mask="YYYY-MM-DD"
          placeholder="--/--/----"
          @update:model-value="updatePeriod('period_end_date')"
        >
          <template #label>
            {{ t("evaluation.labels.termination") }}
          </template>
        </base-date>
      </div>

      <div class="description">
        <base-textarea
          v-model="payload.description"
          :value="payload.description"
          :placeholder="t('common.placeholders.describe')"
          @change="updateDescription()"
        >
          <template #label>
            {{ t("common.labels.description") }}
          </template>
        </base-textarea>
      </div>
    </div>

    <div class="title">
      {{ t("common.labels.infos") }}
    </div>

    <div class="content">
      <div class="infos">
        <div class="group">
          <div class="box progress">
            <label>
              {{ t("common.labels.progress") }}
            </label>
            <div class="value">{{ data.total_progress }}%</div>
          </div>

          <div class="box dates">
            <div class="left">
              <label>
                {{ t("common.labels.dates") }}
              </label>
              <div class="content">
                <div class="item">
                  <span> {{ t("common.labels.start") }}: </span>
                  <strong>
                    {{ getDate(data.start_date) }} -
                    {{ getHour(data.start_date) }}
                  </strong>
                </div>
                <div class="item">
                  <span> {{ t("common.labels.termination") }}: </span>
                  <strong>
                    {{ getDate(data.finish_date) }} -
                    {{ getHour(data.finish_date) }}
                  </strong>
                </div>
              </div>
            </div>
            <div class="right">
              <label>
                {{ t("common.labels.total_duration") }}
              </label>
              <div class="value">
                <svg-icon icon="time-b" size="2rem" />
                <template v-if="!data.start_date">
                  <span> 0m </span>
                </template>

                <template v-if="data.start_date && !data.end_date">
                  <span>
                    {{
                      getDifference(
                        data.start_date,
                        new Date().toISOString(),
                        false,
                      ).text
                    }}
                  </span>
                </template>

                <template v-if="data.start_date && data.end_date">
                  <span>
                    {{
                      getDifference(data.start_date, data.end_date, false).text
                    }}
                  </span>
                </template>
              </div>
            </div>
          </div>
        </div>

        <div class="group">
          <div class="box">
            <label>
              {{ t("common.labels.analysis") }}
            </label>
            <div class="value">
              {{ data.total_analysis }}
            </div>
          </div>

          <div class="box">
            <label>
              {{ t("common.labels.crew") }}
            </label>
            <div class="value">
              {{ data.total_crew }}
            </div>
          </div>

          <div class="box">
            <label>
              {{ t("common.labels.employees") }}
            </label>
            <div class="value">
              {{ data.total_employee }}
            </div>
          </div>

          <div class="box">
            <label>
              {{ t("common.labels.equipments") }}
            </label>
            <div class="value">
              {{ data.total_equipment }}
            </div>
          </div>

          <div class="box">
            <label>
              {{ t("common.labels.samples") }}
            </label>
            <div class="value">
              {{ data.total_sample }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <base-confirm
    :show="showConfirmStart"
    :title="t('evaluation.modal.titles.start_evaluation').toUpperCase()"
    :message="t('evaluation.modal.messages.start_evaluation')"
    @cancel="startEvaluationCancel()"
    @confirm="startEvaluationConfirm()"
  />

  <base-confirm
    :show="showConfirmFinish"
    :title="t('evaluation.modal.titles.finish_evaluation').toUpperCase()"
    :message="t('evaluation.modal.messages.finish_evaluation')"
    @cancel="finishEvaluationCancel()"
    @confirm="finishEvaluationConfirm()"
  />

  <base-confirm
    :show="showConfirmCancel"
    :title="t('evaluation.modal.titles.cancel_evaluation').toUpperCase()"
    :message="t('evaluation.modal.messages.cancel_evaluation')"
    @cancel="cancelEvaluationCancel()"
    @confirm="cancelEvaluationConfirm()"
  />
</template>

<style lang="scss" scoped>
.tab-component {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  .title {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    font-size: 1.15rem;
    font-weight: bold;
    text-transform: uppercase;
    border-bottom: 1px solid var(--border);
    padding-block-end: 0.5rem;
  }

  .box-actions {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 2rem;
    background: var(--white-off);
    padding: 1.25rem;
    border-radius: 0.5rem;
  }

  .box {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 1rem;
    background: var(--background);
    border-radius: 0.5rem;

    label {
      text-transform: uppercase;
    }

    .value {
      font-size: 1.5rem;
      font-weight: bold;
    }
  }

  .infos {
    display: flex;
    flex-direction: column;
    gap: 1.18rem;
    padding-block: 1rem;
  }

  .periods {
    display: flex;
    flex-direction: row;
    gap: 1rem;
  }

  .group {
    display: flex;
    flex-wrap: wrap;
    gap: 1.18rem;
  }

  .description {
    margin-block-start: 1rem;
  }

  .progress {
    flex: 1;
  }

  .dates {
    flex-direction: row;
    flex: 3;

    .left {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      gap: 0.85rem;
      min-width: 16.5rem;

      .content {
        display: flex;
        flex-direction: column;
        font-size: 0.95rem;
        font-weight: normal;
        gap: 0.2rem;

        .item {
          display: grid;
          grid-template-columns: 75px auto;
        }
      }
    }

    .right {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      gap: 0.85rem;
      min-width: 11.5rem;

      .value {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 0.2rem;
      }
    }
  }
}

@media screen and (min-width: 769px) and (max-width: 1024px) {
  .tab-component {
    .periods {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .dates {
      .left {
        min-width: 12.5rem;

        .content {
          font-size: 0.8rem;

          .item {
            grid-template-columns: 1fr 3fr;
            gap: 0.35rem;
          }
        }
      }

      .right {
        min-width: 8rem;

        .value {
          font-size: 1.1rem;

          .svgicon {
            width: 1.5rem;
            height: 1.5rem;
          }
        }
      }
    }
  }
}

@media screen and (max-width: 768px) {
  .tab-component {
    .box-actions {
      flex-direction: column;
      gap: 1.5rem;
    }

    .periods {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .dates {
      flex-direction: column;
      gap: 1rem;
    }
  }
}
</style>
